import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ColumnShape } from 'react-base-table';
import { cloneDeep, flatMap, map } from 'lodash';
import classnames from 'classnames';
import { Tooltip } from 'antd';
import { useDimensionWorkflow } from '@src/hooks';
import { useCreatePermissions } from '@hooks/usePermissions';
import {
  useCustomDimensionManagementList,
  useDeleteCustomDimension,
  useDeploy,
  useUndeploy,
} from '@src/emp-3.0/api/queryHooks';
import { useDeleteVersion } from '@components/metrics-builder/api/queryHooks';
import { DeleteVersionType } from '@src/emp-3.0/components/Management/components/BulkAction/typed';
import { statusOptions } from '@src/emp-3.0/utils/constants';
import { useWorkflowStore } from '@src/pages/workflow/store';
import { IManagementCustomDimension, IManagementCustomDimensionList } from '@src/emp-3.0/types';
import { generateSelectOptions } from '@src/emp-3.0/utils/buildOptions';
import { EPanelOpenMethod } from '@components/block-event-single/store';
import { EFormMode } from '@src/emp-3.0/types/common';
import { useCustomDimensionStore } from '@src/emp-3.0/components/Dimensions/store';
import { categoryMap } from '@src/emp-3.0/constants/dimensions';
import CustomDimensionDetailPanel from '../../../../components/detail-panel/components/CustomDimensionDetailPanel';
import {
  DEFAULT_SELECT_VALUE,
  dateFields,
  handleAction,
  supportSearchFields,
  CUSTOM_DIMENSION,
} from '@src/emp-3.0/constants/management';
import { getFilteredSortedData } from '@src/emp-3.0/utils/management';
import { isDraftVersion, isMultiVersion, isUndeployed } from '@src/emp-3.0/utils';

import {
  ManagementTable,
  useDimensionManagementStore,
  SearchFilterPanel,
  generateColumn,
  managementTableStyles,
  BulkAction,
} from '../';
import { CommonCellRenderer } from '../components/CellRenderers';
import { useNewDataModel } from '@hooks/useNewDataModel';
import { isTraceId } from '@src/emp-3.0/utils';
import { useInitialInfoStore } from '@src/emp-3.0/store/useInitialInfo';
import { workbenchModeTrueButNotWorkbenchAsset } from '../../WorkbenchButton/util';

const Dimension = () => {
  const navigate = useNavigate();
  const isNewWorkflow = useDimensionWorkflow();
  const isNewDataModel = useNewDataModel();
  const hasCreate = useCreatePermissions();
  const { workbenchMode } = useInitialInfoStore();
  const {
    openBulkAction,
    operationMode,
    activeDimension,
    setOpenBulkAction,
    setOperationMode,
    setActiveDimension,

    // Multiple selection operation
    selectedList,
    setSelectedList,
    setRemoveInvalidSelectedList,

    // Search
    searchText,
    setSearchText,

    // Filter conditions
    showFilter,
    status,
    category,
    updateDate,
    deployDate,
    createdBy,
    updatedBy,
    setShowFilter,
    setStatus,
    setCategory,
    setUpdateDate,
    setDeployDate,
    setCreatedBy,
    setUpdatedBy,

    // Sort state
    sort,
    setSort,

    // Reset
    resetFilter,
    resetStore,
  } = useDimensionManagementStore((state) => state);
  const { setFormMode: setWorkflowFormMode } = useWorkflowStore();
  const { resetCustomDimensionStore } = useCustomDimensionStore((state) => state);

  const [dataSource, setDataSource] = useState<any[]>([]);

  const { data: dimensionList, isFetching: isLoadingDimensionList } =
    useCustomDimensionManagementList();
  const { mutate: deployDimension, isLoading: isLoadingDeploy } = useDeploy();
  const { mutate: undeployDimension, isLoading: isLoadingUndeploy } = useUndeploy();
  const { mutate: deleteDimension, isLoading: isLoadingDelete } = useDeleteCustomDimension();
  const { mutate: deleteVersions } = useDeleteVersion();

  const totalCount = useMemo(() => dimensionList?.length ?? 0, [dimensionList]);

  const isLoading = useMemo(() => {
    return isLoadingDimensionList || isLoadingDeploy || isLoadingUndeploy || isLoadingDelete;
  }, [isLoadingDelete, isLoadingDeploy, isLoadingDimensionList, isLoadingUndeploy]);

  const { categoryOptions, createdByOptions, updatedByOptions } = useMemo(
    () => generateSelectOptions(dimensionList, DEFAULT_SELECT_VALUE),
    [dimensionList],
  );

  const sortVersions = (a: IManagementCustomDimension, b: IManagementCustomDimension): number => {
    if (a.is_draft === 1 && b.is_draft === 0) {
      return 1;
    } else if (a.is_draft === 0 && b.is_draft === 1) {
      return -1;
    }
    return 0;
  };

  // Sort the versions within each custom dimension
  const sortCustomDimensions = useMemo(() => {
    return map(cloneDeep(dimensionList ?? []), (dimension) => {
      dimension.versions.sort(sortVersions);
      return dimension;
    });
  }, [dimensionList]);

  const filteredSortedData = useMemo(
    () =>
      getFilteredSortedData<IManagementCustomDimensionList>({
        dataList: sortCustomDimensions,
        filterParams: {
          status,
          category,
          updateDate,
          deployDate,
          createdBy,
          updatedBy,
        },
        searchParams: {
          searchText,
          supportSearchFields,
          dateFields,
        },
        sortParams: sort,
      }),
    [
      category,
      createdBy,
      deployDate,
      searchText,
      sort,
      sortCustomDimensions,
      status,
      updateDate,
      updatedBy,
    ],
  );

  const tableData = useMemo(
    () =>
      flatMap<IManagementCustomDimensionList, any>(filteredSortedData, (element, index) =>
        element.versions.map((version, versionIndex) => {
          const id = `${element.custom_dimension_id}_${version.status}_${versionIndex}`;
          const is_multi_version = element.versions.length > 1;

          return {
            ...version,
            custom_dimension_id: element.custom_dimension_id,
            row_number: index,
            id,
            is_multi_version,
            expandedState: {
              // expanded object key is column dataKey
              associated_flow_names: true,
              mapped_event_names: true,
              associated_metric_names: true,
            },
          };
        }),
      ),
    [filteredSortedData],
  );

  useEffect(() => {
    setDataSource(cloneDeep(tableData));
  }, [tableData]);

  const selectableTableData = useMemo(
    () => dataSource.filter((item) => item.is_predefined !== true),
    [dataSource],
  );

  useEffect(() => {
    setRemoveInvalidSelectedList(selectableTableData);
  }, [setRemoveInvalidSelectedList, selectableTableData]);

  const handleSearch = useCallback(
    (value) => {
      setSearchText(value);
    },
    [setSearchText],
  );

  const handleShowFilterChange = useCallback(
    (value: boolean) => {
      if (!value) {
        resetFilter();
      }
      setShowFilter(value);
    },
    [resetFilter, setShowFilter],
  );

  const handleCloseEventDetailPanel = useCallback(() => {
    setActiveDimension(undefined);
  }, [setActiveDimension]);

  const handleNameClick = useCallback(
    ({ rowData }) => {
      setActiveDimension(rowData);
      setOperationMode(EPanelOpenMethod.VIEW);
    },
    [setActiveDimension, setOperationMode],
  );

  const handleToggleExpanded = ({ rowData }) => {
    dataSource.forEach((d) => {
      if (d.id === rowData?.id) d.expandedState = rowData?.expandedState;
    });
    setDataSource([...dataSource]);
  };

  const handleEdit = useCallback(
    ({ rowData }) => {
      navigate(`/workflow/dimension/${rowData.custom_dimension_id}`);
    },
    [navigate],
  );

  const handleClone = useCallback(
    ({ rowData }) => {
      if (isNewWorkflow) {
        setWorkflowFormMode(EFormMode.CLONE);
        resetCustomDimensionStore();
        navigate(`/workflow/dimension/${rowData.custom_dimension_id}`);
        return;
      }
      setActiveDimension(rowData);
      setOperationMode(EPanelOpenMethod.CLONE);
    },
    [
      navigate,
      isNewWorkflow,
      resetCustomDimensionStore,
      setActiveDimension,
      setOperationMode,
      setWorkflowFormMode,
    ],
  );

  const handleDeployDimension = useCallback(
    ({ rowData }: { rowData: any }) => {
      if (rowData) {
        handleAction({
          action: deployDimension,
          params: {
            resource: 'custom_dimension',
            payload: {
              deploy_all: false,
              ids: [rowData.custom_dimension_id],
            },
          },
          successMessage: 'Deploy Successfully!',
        });
      }
    },
    [deployDimension],
  );

  const handleUndeployDimension = useCallback(
    ({ rowData }: { rowData: any }) => {
      if (rowData) {
        handleAction({
          action: undeployDimension,
          params: {
            resource: 'custom_dimension',
            payload: {
              ids: [rowData.custom_dimension_id],
            },
          },
          successMessage: 'Undeploy Successfully!',
        });
      }
    },
    [undeployDimension],
  );

  const handleDeleteDimension = useCallback(
    ({ rowData }: { rowData: any }) => {
      if (rowData) {
        const isSubVersion = isMultiVersion(rowData) && isDraftVersion(rowData);
        if (isSubVersion) {
          handleAction({
            action: deleteVersions,
            params: { type: DeleteVersionType.custom_dimension, ids: [rowData.version_id] },
            successMessage: 'Delete Successfully!',
          });
        } else {
          handleAction({
            action: deleteDimension,
            params: rowData.custom_dimension_id,
            successMessage: 'Deleted Successfully!',
          });
        }
      }
    },
    [deleteDimension],
  );

  const columns = useMemo<ColumnShape<any>[]>(() => {
    const columnDefinitions: any[] = [
      selectableTableData.length > 0
        ? {
            columnType: 'checkbox',
            type: CUSTOM_DIMENSION,
            dataKey: 'display_name',
            selectedList,
            onChange: ({ selectedList }) => {
              setOpenBulkAction(selectedList.length > 0);
              setSelectedList(selectedList);
            },
            data: selectableTableData,
            disable: (rowData) => workbenchModeTrueButNotWorkbenchAsset(rowData?.is_beta),
          }
        : null,
      { columnType: 'number' },
      {
        columnType: 'name',
        type: CUSTOM_DIMENSION,
        title: 'Custom Dimension',
        key: 'custom-dimension',
        dataKey: 'display_name',
        sort,
        onSortChange: setSort,
        onClick: handleNameClick,
        disable: (rowData) => workbenchModeTrueButNotWorkbenchAsset(rowData?.is_beta),
      },
      {
        columnType: 'status',
        options: statusOptions,
        value: status,
        onChange: setStatus,
        showFilter,
        sort,
        onSortChange: setSort,
      },
      {
        columnType: 'type',
        title: 'Tag Key Type',
        key: `attribute_type`,
        dataKey: 'attribute_type',
        hiddenFilter: true,
        sort,
        onSortChange: setSort,
      },
      {
        columnType: 'list',
        title: 'Mapped Event Names',
        key: 'mapped_event_names',
        dataKey: 'mapped_event_names',
        onClick: handleToggleExpanded,
        placeholder: 'All Applicable Mapped Events',
      },
      ...(isNewDataModel
        ? [
            {
              columnType: 'list',
              title: 'Related Flows',
              key: 'associated_flow_names',
              dataKey: 'associated_flow_names',
              onClick: handleToggleExpanded,
            },
            {
              columnType: 'list',
              title: 'Related Metrics',
              key: 'associated_metric_names',
              dataKey: 'associated_metric_names',
              onClick: handleToggleExpanded,
            },
          ]
        : []),
      {
        columnType: 'common',
        title: 'Mapped Tag Key',
        key: 'mapped-tag-key',
        dataKey: 'attribute_name',
        hiddenFilter: true,
        sort,
        onSortChange: setSort,
      },
      {
        columnType: 'category',
        options: categoryOptions,
        value: category,
        onChange: setCategory,
        showFilter,
        sort,
        onSortChange: setSort,
        cellRenderer: ({ cellData, rowData, ...otherProps }) => {
          let category = categoryMap[cellData] ?? cellData;
          category = rowData?.is_promoted_as_global ? `${category} (Promoted)` : category;
          const tooltipContent = rowData?.is_promoted_as_global
            ? `This tag was first reported for specific events but has been promoted to a Global one. It now applies across all events.`
            : ``;

          return (
            <Tooltip
              title={tooltipContent}
              placement="topLeft"
              overlayStyle={{ minWidth: '250px' }}
            >
              <span style={{ cursor: 'pointer' }}>
                <CommonCellRenderer cellData={category} {...otherProps} />
              </span>
            </Tooltip>
          );
        },
      },
      {
        columnType: 'common',
        title: 'Created By',
        key: 'created by',
        dataKey: 'created_by',
        options: createdByOptions,
        value: createdBy,
        onChange: setCreatedBy,
        showFilter,
        sort,
        onSortChange: setSort,
      },
      {
        columnType: 'common',
        title: 'Updated By',
        key: 'updated by',
        dataKey: 'updated_by',
        options: updatedByOptions,
        value: updatedBy,
        onChange: setUpdatedBy,
        showFilter,
        sort,
        onSortChange: setSort,
      },
      {
        columnType: 'date',
        title: 'Updated At',
        key: 'updated at',
        dataKey: 'updated_at',
        value: updateDate,
        onChange: setUpdateDate,
        showFilter,
        sort,
        onSortChange: setSort,
      },
      {
        columnType: 'date',
        title: 'Deployed At',
        key: 'deployed at',
        dataKey: 'deployed_at',
        value: deployDate,
        onChange: setDeployDate,
        showFilter,
        sort,
        onSortChange: setSort,
      },
    ];
    if (hasCreate) {
      columnDefinitions.push({
        columnType: 'actions',
        dataKey: 'status',
        type: CUSTOM_DIMENSION,
        onEdit: handleEdit,
        onClone: handleClone,
        onDeploy: handleDeployDimension,
        onUndeploy: handleUndeployDimension,
        onDelete: handleDeleteDimension,
      });
    }

    return columnDefinitions.filter(Boolean).map((config) => generateColumn(config));
  }, [
    isNewDataModel,
    selectedList,
    dataSource,
    sort,
    setSort,
    handleNameClick,
    status,
    setStatus,
    showFilter,
    categoryOptions,
    category,
    setCategory,
    createdByOptions,
    createdBy,
    setCreatedBy,
    updatedByOptions,
    updatedBy,
    setUpdatedBy,
    updateDate,
    setUpdateDate,
    deployDate,
    setDeployDate,
    hasCreate,
    setSelectedList,
    handleEdit,
    handleClone,
    handleDeployDimension,
    handleUndeployDimension,
    handleDeleteDimension,
    workbenchMode,
  ]);

  return (
    <ManagementTable
      loading={isLoading}
      columns={columns}
      data={dataSource}
      rowKey="id"
      estimatedRowHeight={50}
      ignoreFunctionInColumnCompare={false}
      header={
        <SearchFilterPanel
          leftComp={
            <>
              <BulkAction
                resource={CUSTOM_DIMENSION}
                data={selectedList}
                show={openBulkAction}
                onClose={() => {
                  setOpenBulkAction(false);
                  setSelectedList([]);
                }}
              />
              {!openBulkAction && (
                <div style={{ fontWeight: 400 }}>{`Total ${totalCount} Custom Dimensions`}</div>
              )}
            </>
          }
          searchValue={searchText}
          onSearch={handleSearch}
          filtersVisible={true}
          showFilters={showFilter}
          onShowFilterChange={handleShowFilterChange}
        />
      }
      detail={
        activeDimension && (
          <CustomDimensionDetailPanel
            id={activeDimension.custom_dimension_id as number}
            data={activeDimension}
            open={!!activeDimension}
            onClose={handleCloseEventDetailPanel}
            operationMode={operationMode}
            setOperationMode={setOperationMode}
          />
        )
      }
      rowClassName={({ rowData }) => {
        return classnames({
          [managementTableStyles.tableRowActive]:
            (activeDimension && activeDimension.id === rowData.id) ||
            selectedList?.find((item) => item.id === rowData.id),
        });
      }}
    />
  );
};

export default Dimension;
